/**
 * CADINU Verified Deploy: everything that talks to the chain goes through the connected wallet's EIP-1193 provider (like the V4
 * MetaMask kit: the page itself opens no RPC connection). The wallet answers eth_chainId from its own setting, while blocks, nonce
 * and gas come from the RPC URL behind that setting, so the network check fingerprints that RPC: genesis hash (for chains on file)
 * and the age of the latest block.
 */
import { getContractAddress, keccak256 } from 'viem'

export type Eip1193 = { request: (args: { method: string; params?: unknown[] }) => Promise<unknown> }

/** Known chains: name, native symbol, explorer, and the genesis hash when it is on file. */
export const CHAINS: Record<number, { name: string; symbol: string; explorer?: string; genesis?: string }> = {
  1: { name: 'Ethereum', symbol: 'ETH', explorer: 'https://etherscan.io', genesis: '0xd4e56740f876aef8c010b86a40d5f56745a118d0906a34e69aec8c0db1cb8fa3' },
  56: { name: 'BNB Smart Chain', symbol: 'BNB', explorer: 'https://bscscan.com', genesis: '0x0d21840abff46b96c84b2ac9e10e4f5cdaeb5693cb665db62a2f3b02d2d57b5b' },
  97: { name: 'BNB Smart Chain Testnet', symbol: 'tBNB', explorer: 'https://testnet.bscscan.com' },
  10: { name: 'OP Mainnet', symbol: 'ETH', explorer: 'https://optimistic.etherscan.io' },
  137: { name: 'Polygon', symbol: 'POL', explorer: 'https://polygonscan.com' },
  8453: { name: 'Base', symbol: 'ETH', explorer: 'https://basescan.org' },
  42161: { name: 'Arbitrum One', symbol: 'ETH', explorer: 'https://arbiscan.io' },
  11155111: { name: 'Sepolia', symbol: 'ETH', explorer: 'https://sepolia.etherscan.io' },
  31337: { name: 'Local test chain', symbol: 'ETH' },
}

export const chainLabel = (id: number) => CHAINS[id]?.name ?? `Chain ${id}`
export const nativeSymbol = (id: number) => CHAINS[id]?.symbol ?? 'native coin'

const HASH_RE = /^0x[0-9a-fA-F]{64}$/
const QTY_RE = /^0[xX][0-9a-fA-F]{1,64}$/
const DATA_RE = /^0x([0-9a-fA-F]{2})*$/
const ADDR_RE = /^0x[0-9a-fA-F]{40}$/

/** Typed readers: the wallet's answers are data too, so each one is shape-checked before use. */
/** What came back, for the error text: the type (and length of a string or array), never the value itself. */
export function shapeOf(v: unknown): string {
  if (v === null) return 'null'
  if (Array.isArray(v)) return `array of ${v.length}`
  if (typeof v === 'string') return `string of ${v.length} chars${v.length && !/^0[xX]/.test(v) ? ', not 0x-prefixed' : ''}`
  if (typeof v === 'number') return Number.isSafeInteger(v) ? (v < 0 ? 'negative number' : 'number') : 'non-integer number'
  return typeof v
}
/**
 * A quantity: a 0x-hex string (JSON-RPC form; leading zeros and 0X tolerated) or, as some wallets and
 * EIP-7702 / smart-account wrappers answer, a non-negative safe-integer JSON number. Everything else is refused.
 */
export function qty(v: unknown, what: string): bigint {
  if (typeof v === 'number' && Number.isSafeInteger(v) && v >= 0) return BigInt(v)
  if (typeof v === 'string' && QTY_RE.test(v)) return BigInt(`0x${v.slice(2)}`)
  throw new Error(`The wallet returned an unexpected ${what} (${shapeOf(v)}).`)
}
export function data(v: unknown, what: string): string {
  if (typeof v !== 'string' || !DATA_RE.test(v)) throw new Error(`The wallet returned unexpected ${what}.`)
  return v.toLowerCase()
}
export function hash(v: unknown, what: string): string {
  if (typeof v !== 'string' || !HASH_RE.test(v)) throw new Error(`The wallet returned an unexpected ${what}.`)
  return v.toLowerCase()
}
export function address(v: unknown, what: string): `0x${string}` {
  if (typeof v !== 'string' || !ADDR_RE.test(v)) throw new Error(`The wallet returned an unexpected ${what}.`)
  return v.toLowerCase() as `0x${string}`
}
const field = (o: unknown, k: string): unknown => (o && typeof o === 'object' && Object.prototype.hasOwnProperty.call(o, k) ? (o as Record<string, unknown>)[k] : undefined)

export type NetworkCheck = {
  chainId: number
  name: string
  latestBlock: bigint
  blockAgeSec: number
  gasPrice: bigint
  genesis: string | null
  genesisStatus: 'match' | 'mismatch' | 'not-on-file'
  problems: string[]
  warnings: string[]
  ok: boolean
}

export async function checkNetwork(p: Eip1193, nowSec = Math.floor(Date.now() / 1000)): Promise<NetworkCheck> {
  const chainId = Number(qty(await p.request({ method: 'eth_chainId' }), 'chain id'))
  const latest = await p.request({ method: 'eth_getBlockByNumber', params: ['latest', false] })
  const latestBlock = qty(field(latest, 'number'), 'block number')
  const ts = Number(qty(field(latest, 'timestamp'), 'block timestamp'))
  let genesis: string | null = null
  try {
    genesis = hash(field(await p.request({ method: 'eth_getBlockByNumber', params: ['0x0', false] }), 'hash'), 'genesis hash')
  } catch {
    genesis = null
  }
  const gasPrice = qty(await p.request({ method: 'eth_gasPrice' }), 'gas price')
  const known = CHAINS[chainId]
  const problems: string[] = []
  const warnings: string[] = []
  let genesisStatus: NetworkCheck['genesisStatus'] = 'not-on-file'
  if (known?.genesis) {
    genesisStatus = genesis === known.genesis ? 'match' : 'mismatch'
    if (genesisStatus === 'mismatch')
      problems.push(`The wallet says chain ${chainId} (${known.name}), but its RPC answers with another chain's genesis block. Fix the network's RPC URL in the wallet.`)
  }
  const blockAgeSec = nowSec - ts
  if (Math.abs(blockAgeSec) > 120) warnings.push(`The latest block is ${blockAgeSec} seconds old: the wallet's RPC is stale or a local fork.`)
  if (!known) warnings.push(`Chain ${chainId} is not on this page's list; check the network name in your wallet.`)
  return { chainId, name: chainLabel(chainId), latestBlock, blockAgeSec, gasPrice, genesis, genesisStatus, problems, warnings, ok: problems.length === 0 }
}

export async function getCode(p: Eip1193, a: string): Promise<string> {
  return data(await p.request({ method: 'eth_getCode', params: [a, 'latest'] }), 'contract code')
}
export async function getNonce(p: Eip1193, a: string): Promise<number> {
  return Number(qty(await p.request({ method: 'eth_getTransactionCount', params: [a, 'pending'] }), 'nonce'))
}
export async function getBalance(p: Eip1193, a: string): Promise<bigint> {
  return qty(await p.request({ method: 'eth_getBalance', params: [a, 'latest'] }), 'balance')
}
export async function getAccounts(p: Eip1193): Promise<string[]> {
  const r = await p.request({ method: 'eth_accounts' })
  return Array.isArray(r) ? r.filter((x): x is string => typeof x === 'string' && ADDR_RE.test(x)).map((x) => x.toLowerCase()) : []
}

export const predictAddress = (from: string, nonce: number) => getContractAddress({ from: from as `0x${string}`, nonce: BigInt(nonce) })
export const hashHex = (hex: string) => keccak256(`0x${hex}` as `0x${string}`)

export async function estimateGas(p: Eip1193, tx: { from: string; data: string; value: bigint }): Promise<bigint> {
  return qty(await p.request({ method: 'eth_estimateGas', params: [{ from: tx.from, data: tx.data, value: `0x${tx.value.toString(16)}` }] }), 'gas estimate')
}

export async function sendDeploy(p: Eip1193, tx: { from: string; data: string; value: bigint; gas: bigint }): Promise<string> {
  // no "to": a contract creation. The wallet shows it and the user signs it there.
  const h = await p.request({
    method: 'eth_sendTransaction',
    params: [{ from: tx.from, data: tx.data, value: `0x${tx.value.toString(16)}`, gas: `0x${tx.gas.toString(16)}` }],
  })
  return hash(h, 'transaction hash')
}

export type Receipt = { status: 'success' | 'reverted'; contractAddress: string | null; blockNumber: bigint; gasUsed: bigint; from: string }
export async function getReceipt(p: Eip1193, txHash: string): Promise<Receipt | null> {
  const r = await p.request({ method: 'eth_getTransactionReceipt', params: [txHash] })
  if (!r) return null
  const ca = field(r, 'contractAddress')
  return {
    status: field(r, 'status') === '0x1' ? 'success' : 'reverted',
    contractAddress: typeof ca === 'string' && ADDR_RE.test(ca) ? ca.toLowerCase() : null,
    blockNumber: qty(field(r, 'blockNumber'), 'block number'),
    gasUsed: qty(field(r, 'gasUsed'), 'gas used'),
    from: address(field(r, 'from'), 'sender'),
  }
}
export async function getTxInput(p: Eip1193, txHash: string): Promise<{ input: string; from: string; to: string | null } | null> {
  const t = await p.request({ method: 'eth_getTransactionByHash', params: [txHash] })
  if (!t) return null
  const to = field(t, 'to')
  return { input: data(field(t, 'input'), 'transaction input'), from: address(field(t, 'from'), 'sender'), to: typeof to === 'string' ? to.toLowerCase() : null }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))
export async function waitReceipt(p: Eip1193, txHash: string, { tries = 400, everyMs = 1500, signal }: { tries?: number; everyMs?: number; signal?: { stop: boolean } } = {}) {
  for (let i = 0; i < tries; i++) {
    if (signal?.stop) throw new Error('Stopped waiting.')
    // eslint-disable-next-line no-await-in-loop
    const r = await getReceipt(p, txHash)
    if (r) return r
    // eslint-disable-next-line no-await-in-loop
    await sleep(everyMs)
  }
  throw new Error(`No receipt after ${Math.round((tries * everyMs) / 60000)} minutes. Paste the transaction hash below to check it later.`)
}
